import { brandPrimaryHover, header, warningDark } from '@Styles/colors';
import { CssSelectors } from '@Styles/styles';
import { StyleSheet } from 'aphrodite';
import { THEME } from '../../../../../web/components-v2/Theme/theme';

const { spacing, colors, common, typography } = THEME;
const { fontSizes } = typography;

// Column widths for the Run card stage rows; the breakdown sub-line indents to align with Records
const STAGE_LABEL_WIDTH = 110;
const STAGE_DURATION_WIDTH = 130;

// Tracking shared by uppercase metadata labels (RUN ID, STAGE, STARTED, etc.)
const UPPERCASE_TRACKING = '0.5px';

// Visible focus ring used on collapsible triggers; matches brand primary for keyboard nav
const FOCUS_OUTLINE = `2px solid ${THEME.colors.primary.main}`;

export const styleSheet = StyleSheet.create({
	container: {
		padding: 16,
	},
	tabViewTabs: {
		display: 'flex',
		borderBottom: '1px solid #e2e8f0',
	},
	tabViewItem: {
		borderBottom: `1px solid #D3DBDE `,
		color: header,
		display: 'inline-flex',
		flexBasis: 'auto',
		fontSize: 14,
		height: 44,
		padding: '0 36px',
		textAlign: 'center',
		textTransform: 'uppercase',
		whiteSpace: 'nowrap',
		alignItems: 'center',
	},
	section: {
		padding: 16,
	},
	header: {
		display: 'flex',
		gap: 16,
		alignItems: 'center',
		justifyContent: 'flex-end',
		marginBottom: 16,
		width: '100%',
	},
	noResults: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: 16,
	},
	tableContainer: {
		display: 'flex',
		flexDirection: 'column',
		minHeight: '80%',
	},
	loadingRow: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		height: '100%',
	},
	tableOverrides: {
		width: '100%',
		tableLayout: 'fixed',
	},
	statusIndicator: {
		alignItems: 'center',
		display: 'flex',
		...CssSelectors.allChildren(
			{
				height: 20,
				margin: '0 10px 0 -30px',
				width: 20,
			},
			'span'
		),
	},
	statusIndicatorActive: {
		marginRight: 10,
	},
	statusIndicatorInactive: {
		opacity: 0.5,
	},
	etlStagesContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: 16,
		padding: 20,
	},
	confirmationDialogDescription: {
		fontSize: 14,
		color: '#333',
		marginBottom: spacing.sm,
		textAlign: 'center',
		maxWidth: 600,
	},
	icons: {
		height: 17,
		width: 17,
		marginLeft: spacing.sm,
		display: 'inline-block',
		verticalAlign: 'middle',
	},
	copyButton: {
		alignItems: 'center',
		display: 'flex',
		gap: 5,
	},
	filterContainer: {
		display: 'flex',
		flexDirection: 'row',
		gap: 10,
		alignItems: 'center',
		marginBottom: spacing.md,
	},
	selectContainer: {
		width: 200,
	},
	clearSearch: {
		background: 'transparent',
		border: 'none',
		color: brandPrimaryHover,
		cursor: 'pointer',
		fontSize: 14,
		fontWeight: 500,
		padding: '8px 12px',
		textDecoration: 'none',
		':hover': {
			textDecoration: 'underline',
		},
	},
	emptyState: {
		color: '#666',
		fontSize: 14,
	},
	popoverContent: {
		padding: spacing.md,
		minWidth: 'auto',
	},
	dateRangeContainer: {
		display: 'flex',
		flexDirection: 'row',
		gap: spacing.md,
	},
	presetButtons: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.xs,
	},
	calendarContainer: {
		display: 'flex',
		justifyContent: 'center',
	},
	buttonContainer: {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: spacing.xs,
		marginTop: spacing.md,
		paddingTop: spacing.md,
		borderTop: '1px solid #e2e8f0',
	},
	setupIndicator: {
		width: 8,
		height: 8,
		borderRadius: '50%',
		backgroundColor: warningDark,
		marginLeft: spacing.xs,
		display: 'inline-block',
		verticalAlign: 'middle',
	},
	setupRequiredBanner: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: spacing.sm,
		padding: `${spacing.sm}px ${spacing.md}px`,
		backgroundColor: colors.warning.light,
		borderRadius: 6,
		marginBottom: spacing.md,
		border: `1px solid ${warningDark}`,
	},
	setupRequiredIcon: {
		width: 20,
		height: 20,
		flexShrink: 0,
		// Nudge to align visual center of icon with the cap-height of the first line
		marginTop: 1,
	},
	setupRequiredText: {
		fontSize: 14,
		color: warningDark,
		lineHeight: 1.4,
	},
	setupRequiredHeading: {
		fontWeight: 600,
		marginBottom: spacing.xs / 2,
	},
	setupRequiredList: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.xs / 2,
	},
	setupRequiredListItem: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: spacing.xs,
	},
	setupRequiredBullet: {
		flexShrink: 0,
		lineHeight: 1.4,
	},
	customerActorPill: {
		alignSelf: 'center',
		backgroundColor: colors.warning.main,
		borderRadius: 999,
		color: colors.warning.contrastText,
		cursor: 'help',
		flexShrink: 0,
		fontSize: 11,
		fontWeight: 600,
		letterSpacing: 0.3,
		lineHeight: 1,
		marginLeft: spacing.xs,
		paddingBottom: 3,
		paddingLeft: 8,
		paddingRight: 8,
		paddingTop: 3,
		textTransform: 'uppercase',
		whiteSpace: 'nowrap',
	},
	setupRequiredJumpButton: {
		background: 'none',
		border: 'none',
		color: warningDark,
		cursor: 'pointer',
		font: 'inherit',
		lineHeight: 1.4,
		padding: 0,
		textAlign: 'left',
		textDecoration: 'underline',
		':hover': {
			textDecoration: 'none',
		},
		':focus-visible': {
			outline: `2px solid ${warningDark}`,
			outlineOffset: 2,
			borderRadius: 2,
		},
	},
	setupRequiredLink: {
		background: 'none',
		border: 'none',
		color: warningDark,
		cursor: 'pointer',
		font: 'inherit',
		fontWeight: 600,
		padding: 0,
		textDecoration: 'underline',
		':hover': {
			textDecoration: 'none',
		},
	},
	stageBadge: {
		alignItems: 'center',
		backgroundColor: colors.pill.gray.background,
		borderRadius: common.borderRadius,
		color: colors.text.main,
		display: 'inline-flex',
		fontFamily: 'monospace',
		fontSize: fontSizes.xxs,
		fontWeight: 600,
		height: 20,
		justifyContent: 'center',
		letterSpacing: '1px',
		// Fixed width so 1-letter (L) and 4-letter (PETL) badges align across cards
		minWidth: 56,
		padding: `0 ${spacing.sm}px`,
	},
	modeBadge: {
		alignItems: 'center',
		border: '1px solid transparent',
		borderRadius: common.borderRadius,
		display: 'inline-flex',
		fontSize: fontSizes.xxs,
		fontWeight: 600,
		height: 20,
		justifyContent: 'center',
		letterSpacing: '0.3px',
		// Fixed width so FULL SYNC and INCREMENTAL SYNC don't shift the trailing metadata
		minWidth: 140,
		padding: `0 ${spacing.sm}px`,
		textTransform: 'uppercase',
	},
	modeBadgeFull: {
		backgroundColor: colors.success.subtle.light,
		borderColor: colors.success.subtle.main,
		color: colors.success.subtle.dark,
	},
	modeBadgeIncremental: {
		backgroundColor: colors.pill.blue.background,
		borderColor: colors.pill.blue.border,
		color: colors.pill.blue.icon,
	},
	modeBadgeWithTooltip: {
		cursor: 'help',
	},
	modeBadgeInfo: {
		alignItems: 'center',
		display: 'inline-flex',
		marginLeft: spacing.xs,
		opacity: 0.7,
		// InfoIcon ships at 14x14; scale to ~12 to fit comfortably in the 20px badge
		...CssSelectors.allChildren({ width: 12, height: 12 }, 'svg'),
	},
	runIdLabel: {
		color: colors.text.light,
		display: 'block',
		fontSize: fontSizes.xs,
		fontWeight: 600,
		letterSpacing: UPPERCASE_TRACKING,
		lineHeight: 1,
		marginBottom: spacing.xs / 2,
		textTransform: 'uppercase',
	},
	activitySection: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.sm,
		marginTop: spacing.lg,
	},
	sectionLabel: {
		color: colors.text.main,
		fontSize: fontSizes.sm,
		fontWeight: 600,
		letterSpacing: UPPERCASE_TRACKING,
		margin: 0,
		textTransform: 'uppercase',
	},
	ungroupedSectionTrigger: {
		alignItems: 'center',
		background: 'transparent',
		border: 'none',
		cursor: 'pointer',
		display: 'flex',
		gap: spacing.sm,
		padding: 0,
		textAlign: 'left',
		width: 'fit-content',
		':hover': {
			color: colors.hover.main,
		},
		':focus-visible': {
			borderRadius: common.borderRadius,
			outline: FOCUS_OUTLINE,
			outlineOffset: 2,
		},
	},
	ungroupedSectionCount: {
		color: colors.text.light,
		fontSize: fontSizes.xs,
	},
	ungroupedTable: {
		marginTop: spacing.sm,
	},
	runCardList: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.sm,
	},
	runCard: {
		backgroundColor: colors.background.light,
		border: common.border,
		borderRadius: common.borderRadius,
		overflow: 'hidden',
		transition: 'box-shadow 150ms ease, border-color 150ms ease',
	},
	runCardOpen: {
		borderColor: colors.border.main,
	},
	runCardHeader: {
		alignItems: 'center',
		display: 'flex',
		gap: spacing.md,
		padding: `${spacing.sm}px ${spacing.md}px`,
	},
	runCardTrigger: {
		alignItems: 'center',
		background: 'transparent',
		border: 'none',
		cursor: 'pointer',
		display: 'flex',
		flex: 1,
		gap: spacing.md,
		minWidth: 0,
		padding: 0,
		textAlign: 'left',
		':hover': {
			color: colors.hover.main,
		},
		':focus-visible': {
			borderRadius: common.borderRadius,
			outline: FOCUS_OUTLINE,
			outlineOffset: 2,
		},
	},
	runCardChevron: {
		alignItems: 'center',
		color: colors.icon.gray,
		display: 'inline-flex',
		flexShrink: 0,
		transform: 'rotate(-90deg)',
		transition: 'transform 150ms ease',
	},
	runCardChevronOpen: {
		transform: 'rotate(0deg)',
	},
	runCardHeaderLine: {
		alignItems: 'center',
		display: 'flex',
		flex: 1,
		flexWrap: 'wrap',
		gap: spacing.md,
		minWidth: 0,
	},
	runCardFieldGroup: {
		alignItems: 'baseline',
		display: 'inline-flex',
		gap: spacing.xs + 2,
	},
	runCardFieldLabel: {
		color: colors.text.light,
		fontSize: fontSizes.xs,
		fontWeight: 600,
		letterSpacing: UPPERCASE_TRACKING,
		textTransform: 'uppercase',
	},
	runCardSummaryItem: {
		color: colors.text.main,
		fontSize: fontSizes.sm,
	},
	runCardSeparator: {
		color: colors.border.main,
		fontSize: fontSizes.sm,
	},
	runCardHeaderRight: {
		alignItems: 'flex-end',
		display: 'flex',
		flexDirection: 'column',
		flexShrink: 0,
		gap: spacing.xs / 2,
	},
	runCardStages: {
		backgroundColor: colors.background.main,
		borderTop: common.border,
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.xs,
		padding: `${spacing.sm}px ${spacing.md}px ${spacing.sm}px ${spacing.lg + spacing.sm}px`,
	},
	runStageHeader: {
		alignItems: 'center',
		borderBottom: common.border,
		display: 'flex',
		gap: spacing.md,
		paddingBottom: spacing.xs,
	},
	runStageHeaderCell: {
		color: colors.text.light,
		fontSize: fontSizes.xs,
		fontWeight: 600,
		letterSpacing: UPPERCASE_TRACKING,
		textTransform: 'uppercase',
	},
	runStageRowGroup: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.xs / 2,
	},
	runStageRow: {
		alignItems: 'center',
		display: 'flex',
		fontSize: fontSizes.sm,
		gap: spacing.md,
	},
	runStageBreakdown: {
		color: colors.text.light,
		display: 'flex',
		fontSize: fontSizes.xs,
		gap: spacing.xs + 2,
		// Indent matches the start of the Records column (Stage minWidth + Duration minWidth + 2 gaps)
		paddingLeft: STAGE_LABEL_WIDTH + spacing.md + STAGE_DURATION_WIDTH + spacing.md,
	},
	runStageLabel: {
		color: colors.text.dark,
		fontWeight: 600,
		minWidth: STAGE_LABEL_WIDTH,
	},
	runStageDuration: {
		color: colors.text.main,
		minWidth: STAGE_DURATION_WIDTH,
	},
	runStageTotal: {
		color: colors.text.main,
		flex: 1,
	},
	runStageCopyJob: {
		alignItems: 'center',
		background: 'transparent',
		border: 'none',
		color: colors.icon.gray,
		cursor: 'pointer',
		display: 'inline-flex',
		marginLeft: spacing.xs,
		opacity: 0.7,
		padding: 0,
		verticalAlign: 'middle',
		':hover': {
			opacity: 1,
		},
		// ClipboardIcon ships at 15x19; scale to match the 14px stage label x-height
		...CssSelectors.allChildren({ width: 12, height: 15 }, 'svg'),
	},
});
